/**
 * Constantes centralizadas da aplicação
 * 
 * Este arquivo centraliza todas as constantes usadas na aplicação,
 * facilitando manutenção e consistência.
 */

// ============================================================================
// CORES
// ============================================================================

export const COLORS = {
  // Cores primárias
  primary: '#1976d2',
  secondary: '#dc004e',
  
  // Cores de severidade/prioridade
  severity: {
    high: '#f44336',
    medium: '#ff9800',
    low: '#2196f3',
    urgent: '#f44336',
    alta: '#ff9800',
    media: '#2196f3',
    baixa: '#9e9e9e',
  },
  
  // Cores de status
  status: {
    pending: '#f44336',      // Aberta
    inProgress: '#2196f3',    // Em Andamento
    completed: '#4caf50',     // Concluída
    cancelled: '#9e9e9e',     // Cancelada
  },
  
  // Cores de categorias
  category: {
    manutencao: '#ff9800',
    limpeza: '#4caf50',
    seguranca: '#f44336',
    infraestrutura: '#2196f3',
    areasComuns: '#9c27b0',
    outros: '#607d8b',
  },
  
  // Cores para gráficos
  chart: {
    primary: '#1976d2',
    secondary: '#dc004e',
    success: '#4caf50',
    warning: '#ff9800',
    error: '#f44336',
    info: '#2196f3',
  },
  
  // Cores neutras
  neutral: {
    light: '#f1f1f1',
    medium: '#c1c1c1',
    dark: '#a8a8a8',
    text: '#666',
    white: '#ffffff',
  },
  
  // Cores de drag and drop
  drag: {
    over: '#e3f2fd',
    default: '#ffffff',
  },
} as const;

// ============================================================================
// ROTAS
// ============================================================================

export const ROUTES = {
  // Rotas públicas
  HOME: '/',
  LOGIN: '/login',
  UNAUTHORIZED: '/unauthorized',
  PUBLIC_DEMAND: '/public-demand',
  PUBLIC_SUGGESTION_BOX: '/sugestoes',
  /** Painel Público da Operação (Relatório B — "Desempenho & Transparência"), sem login. */
  PUBLIC_OPERATION_REPORT: '/operacao',
  PROVIDER_COMPLETION_LINK: '/concluir-demanda',
  PRIVACY_POLICY: '/privacy-policy',
  /** Contato institucional (dados vindos da API pública; mesma origem que a landing). */
  CONTACT: '/contato',
  ACCOUNT_DELETION_INFO: '/account-deletion',
  SYSTEM_ADMIN_SELECT: '/system-admin/select',
  SYSTEM_ADMIN_AUDIT: '/system-admin/audit',
  PAYMENT: '/payment',
  GO_FINANCE: '/go/finance',
  
  // Rotas da aplicação
  APP: '/app',
  ACCOUNT_DELETION: '/app/account-deletion',
  APP_CLIENTS: '/app/clients',
  APP_USERS: '/app/users',
  APP_ROLES: '/app/roles',
  APP_CONFIG: '/app/config',
  APP_CONFIG_BILLING: '/app/finance', // Redireciona para Financeiro (antiga aba Faturamento)
  APP_PROFILE: '/app/profile',
  APP_REPORTS: '/app/reports',
  APP_SUGGESTIONS: '/app/suggestions',
  APP_PROVIDERS: '/app/providers',
  APP_CHECKLISTS: '/app/checklists',
  APP_PATRIMONY: '/app/patrimony',
  // Pedido do usuário (08/09): conferência avulsa não abre mais Demand — a rota é por id da
  // própria inspeção (ver PatrimonyConference.tsx).
  APP_PATRIMONY_CONFERENCE: (inspectionId: string) => `/app/patrimony/conference/${inspectionId}`,
  // Pedido do usuário (04/09): QR Codes, Demandas Recorrentes, Caixa de Sugestões e Painel
  // Público deixam de ser modais abertos a partir da linha do condomínio (ClientManagementPage)
  // e viram páginas de 1º nível, condomínio-scoped — mesmo padrão de Checklists/Patrimônio.
  APP_QR_CODES: '/app/qr-codes',
  APP_RECURRING_DEMANDS: '/app/recurring-demands',
  APP_SUGGESTION_BOX: '/app/suggestion-box',
  APP_PUBLIC_PANEL: '/app/public-panel',
  APP_RESOURCES: '/app/resources',
  APP_FINANCE: '/app/finance',
  APP_DEMANDS: '/app',
  
  // Rotas com parâmetros
  PUBLIC_DEMAND_WITH_ID: (clientId: string) => `/public-demand/${clientId}`,
  PUBLIC_SUGGESTION_BOX_WITH_TOKEN: (token: string) => `/sugestoes/${token}`,
  PUBLIC_OPERATION_REPORT_WITH_TOKEN: (token: string) => `/operacao/${token}`,
} as const;

/** Site institucional (landing) — redirecionamentos pós-fluxos sensíveis (ex.: exclusão de conta). */
export const VOVER_LANDING_URL = 'https://vover.com.br';

/** Package name do app na Google Play (listing: {@link GOOGLE_PLAY_APP_URL}). */
export const GOOGLE_PLAY_PACKAGE_ID = 'com.vover.mobile' as const;

/** URL pública da ficha do app na Google Play. */
export const GOOGLE_PLAY_APP_URL =
  `https://play.google.com/store/apps/details?id=${GOOGLE_PLAY_PACKAGE_ID}` as const;

/** URL pública da ficha do app na App Store. */
export const APP_STORE_APP_URL = 'https://apps.apple.com/app/vover/id6767202168' as const;

/** sessionStorage: plano e valores da calculadora da landing para pré-preencher o cadastro. */
export const CALCULATOR_SIGNUP_PREFILL_STORAGE_KEY = 'vover_signup_calculator_prefill_v1';

/** sessionStorage: JWT pós-2FA do system admin (fallback quando cookie cross-origin falha). */
export const SYSTEM_ADMIN_TOKEN_STORAGE_KEY = 'vover_system_admin_access_token';

// ============================================================================
// MENSAGENS
// ============================================================================

export const MESSAGES = {
  ERRORS: {
    NETWORK: 'Erro de conexão. Verifique sua internet.',
    BACKEND_UNAVAILABLE: 'Não foi possível conectar ao servidor. Tente novamente mais tarde.',
    UNAUTHORIZED: 'Sessão expirada. Faça login novamente.',
    NOT_FOUND: 'Recurso não encontrado.',
    SERVER_ERROR: 'Erro no servidor. Tente novamente mais tarde.',
    LOAD_ERROR: 'Erro ao carregar dados.',
    SAVE_ERROR: 'Erro ao salvar dados.',
    DELETE_ERROR: 'Erro ao excluir dados.',
    UPDATE_ERROR: 'Erro ao atualizar dados.',
    UPDATE_DEMAND_ERROR: 'Erro ao atualizar demanda',
    UPDATE_STATUS_ERROR: 'Erro ao atualizar status da demanda',
    UPDATE_PRIORITY_ERROR: 'Erro ao atualizar prioridade da demanda',
    MOVE_DEMAND_ERROR: 'Erro ao mover demanda',
    INVALID_DATE: 'Data inválida. Use o formato DD/MM/YYYY',
    PAST_DATE: 'A data não pode ser anterior ao dia atual',
    INVALID_DESCRIPTION: 'A descrição deve ter pelo menos 10 caracteres',
    MISSING_CATEGORY: 'Selecione uma categoria',
    MISSING_PRIORITY: 'Selecione uma prioridade',
    DEMAND_NOT_FOUND: 'Demanda não encontrada',
    USER_LIST_ERROR: 'Erro ao carregar lista de usuários',
    ASSIGN_ERROR: 'Erro ao atribuir usuário',
    RECURRENCE_ERROR: 'Erro ao carregar recorrências',
    INVALID_CNPJ: 'CNPJ inválido. Verifique o formato.',
    INVALID_CNPJ_AFTER_CLEAN: 'CNPJ inválido após limpeza. Verifique o formato.',
    UNITS_REQUIRED: 'Quantidade de unidades é obrigatória',
    UNITS_INVALID: 'Quantidade de unidades deve ser um número maior que zero',
    UNITS_INVALID_FORMAT: 'Quantidade de unidades inválida',
    FIELDS_REQUIRED: 'Preencha todos os campos obrigatórios',
    QR_CODE_ERROR: 'Erro ao gerar QR Code',
    QR_CODE_NOT_GENERATED: 'QR Code não foi gerado ainda',
    CEP_API_UNAVAILABLE: 'Serviço de consulta de CEP temporariamente indisponível. Por favor, preencha o endereço manualmente.',
  },
  
  SUCCESS: {
    SAVED: 'Salvo com sucesso!',
    DELETED: 'Excluído com sucesso!',
    UPDATED: 'Atualizado com sucesso!',
    CREATED: 'Demanda criada com sucesso!',
    MOVED: 'Demanda movida com sucesso!',
    LOGIN: 'Login realizado com sucesso!',
    SIGNUP: 'Conta criada com sucesso! Faça login para continuar.',
    SIGNUP_AND_LOGIN: 'Conta criada com sucesso! Fazendo login...',
    DEMAND_CREATED: 'Demanda criada com sucesso!',
    DEMAND_UPDATED: 'Demanda atualizada com sucesso!',
    DEMAND_DELETED: 'Demanda excluída com sucesso!',
    PRIORITY_UPDATED: 'Prioridade da demanda atualizada com sucesso!',
    CLIENT_CREATED: 'Cliente criado com sucesso!',
    CLIENT_UPDATED: 'Cliente atualizado com sucesso!',
    CLIENT_DELETED: 'Cliente excluído com sucesso!',
    CLIENT_ACTIVATED: 'Cliente ativado com sucesso!',
    CLIENT_DEACTIVATED: 'Cliente desativado com sucesso!',
    ADDRESS_FILLED: 'Endereço preenchido automaticamente',
    QR_CODE_DOWNLOADED: 'QR Code baixado com sucesso!',
  },
  
  WARNINGS: {
    ASSIGN_WARNING: 'Demanda movida, mas não foi possível atribuir o responsável automaticamente',
    CEP_INCOMPLETE: 'CEP encontrado mas com dados incompletos',
    CEP_NOT_FOUND: 'CEP não encontrado',
  },
  
  INFO: {
    LOADING: 'Carregando...',
  },
} as const;

// ============================================================================
// CONFIGURAÇÕES
// ============================================================================

export const CONFIG = {
  // Timeouts
  API_TIMEOUT: 10000, // 10 segundos
  API_SIGNUP_TIMEOUT: 60000, // 60 segundos (signup cria tenant + configs e pode demorar)
  DEBOUNCE_DELAY: 300, // 300ms
  
  // Arquivos
  MAX_FILE_SIZE: 5 * 1024 * 1024, // 5MB
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  
  // Validações
  MIN_DESCRIPTION_LENGTH: 10,
  MAX_DESCRIPTION_LENGTH: 5000,
  MIN_PASSWORD_LENGTH: 8,
  
  // Paginação
  DEFAULT_PAGE_SIZE: 10,
  MAX_PAGE_SIZE: 100,
  
  // Datas
  DATE_FORMAT: 'dd/MM/yyyy',
  DATE_TIME_FORMAT: 'dd/MM/yyyy HH:mm',
  
  // Outros
  DEFAULT_PRIORITY: 'media',
  DEFAULT_PERIOD: 'all',

  // URLs de fallback (desenvolvimento)
  DEFAULT_PUBLIC_FORM_BASE_URL: 'http://localhost:3010',
} as const;

// ============================================================================
// VALORES PADRÃO
// ============================================================================

export const DEFAULTS = {
  // Prioridades
  PRIORITIES: [
    { key: 'baixa', name: 'Baixa', color: COLORS.severity.baixa },
    { key: 'media', name: 'Média', color: COLORS.severity.media },
    { key: 'alta', name: 'Alta', color: COLORS.severity.alta },
    { key: 'urgente', name: 'Urgente', color: COLORS.severity.urgent },
  ],
  
  // Status/Colunas
  COLUMNS: [
    { key: 'pending', name: 'Aberta', color: COLORS.status.pending },
    { key: 'in_progress', name: 'Em Andamento', color: COLORS.status.inProgress },
    { key: 'completed', name: 'Concluída', color: COLORS.status.completed },
    { key: 'cancelled', name: 'Cancelada', color: COLORS.status.cancelled },
  ],
  
  // Categorias
  CATEGORIES: [
    { key: 'manutencao', name: 'Manutenção', color: COLORS.category.manutencao },
    { key: 'limpeza', name: 'Limpeza', color: COLORS.category.limpeza },
    { key: 'seguranca', name: 'Segurança', color: COLORS.category.seguranca },
    { key: 'infraestrutura', name: 'Infraestrutura', color: COLORS.category.infraestrutura },
    { key: 'areas_comuns', name: 'Áreas Comuns', color: COLORS.category.areasComuns },
    { key: 'outros', name: 'Outros', color: COLORS.category.outros },
  ],
} as const;

// ============================================================================
// LABELS DE SEVERIDADE (Análise de Tendências)
// ============================================================================

export const SEVERITY_LABELS = {
  CRITICAL: 'CRÍTICO',
  ATTENTION: 'ATENÇÃO',
  MONITOR: 'MONITORAR',
} as const;

